/* ═══════════════════════════════════════════
   Theme Presets & Design Tokens
   ═══════════════════════════════════════════ */

export interface ThemeColors {
  primary: string;
  "primary-light": string;
  secondary: string;
  "secondary-light": string;
  bg: string;
  surface: string;
  "surface-alt": string;
  text: string;
  "text-muted": string;
  border: string;
  success: string;
  warning: string;
  /** Between danger and warning: the "high" rung of a four-step severity scale. */
  severe: string;
  danger: string;
  info: string;
}

export interface ThemePreset {
  id: string;
  name: string;
  nameAr: string;
  light: ThemeColors;
  dark: ThemeColors;
}

export interface ThemeSettings {
  preset: string;
  colors: Partial<ThemeColors>;
  fonts: {
    heading: string;
    body: string;
  };
  layout: {
    sidebarStyle: "full" | "mini";
    contentMaxWidth: string;
    cardRadius: string;
    density: "comfortable" | "normal" | "compact";
  };
  darkMode: {
    enabled: boolean;
    default: "light" | "dark" | "system";
  };
  ui: {
    buttonStyle: "rounded" | "pill" | "square";
    buttonSize: "sm" | "md" | "lg";
    iconStyle: "rounded" | "square";
    iconSize: "sm" | "md" | "lg";
  };
  custom_css: string;
}

/* ── الثيمات الجاهزة ── */
export const PRESETS: ThemePreset[] = [
  {
    id: "smart-blue",
    name: "Smart Blue",
    nameAr: "أزرق ذهبي",
    light: {
      primary: "30 58 95",
      "primary-light": "59 130 246",
      secondary: "200 169 81",
      "secondary-light": "240 215 140",
      bg: "248 249 252",
      surface: "255 255 255",
      "surface-alt": "241 245 249",
      text: "30 41 59",
      "text-muted": "100 116 139",
      border: "226 232 240",
      success: "5 150 105",
      warning: "217 119 6",
      severe: "234 88 12",
      danger: "220 38 38",
      info: "2 132 199",
    },
    dark: {
      primary: "96 165 250",
      "primary-light": "147 197 253",
      secondary: "240 215 140",
      "secondary-light": "253 230 138",
      bg: "15 23 42",
      surface: "30 41 59",
      "surface-alt": "51 65 85",
      text: "241 245 249",
      "text-muted": "148 163 184",
      border: "51 65 85",
      success: "52 211 153",
      warning: "251 191 36",
      severe: "251 146 60",
      danger: "248 113 113",
      info: "56 189 248",
    },
  },
  {
    id: "olive-green",
    name: "Olive Green",
    nameAr: "أخضر زيتوني",
    light: {
      primary: "27 67 50",
      "primary-light": "34 197 94",
      secondary: "149 213 178",
      "secondary-light": "187 247 208",
      bg: "247 250 248",
      surface: "255 255 255",
      "surface-alt": "240 253 244",
      text: "20 51 35",
      "text-muted": "75 85 72",
      border: "209 233 218",
      success: "22 163 74",
      warning: "202 138 4",
      severe: "234 88 12",
      danger: "185 28 28",
      info: "3 105 161",
    },
    dark: {
      primary: "74 222 128",
      "primary-light": "134 239 172",
      secondary: "187 247 208",
      "secondary-light": "220 252 231",
      bg: "5 20 14",
      surface: "20 51 35",
      "surface-alt": "34 80 56",
      text: "236 253 245",
      "text-muted": "156 192 172",
      border: "34 80 56",
      success: "52 211 153",
      warning: "251 191 36",
      severe: "251 146 60",
      danger: "248 113 113",
      info: "56 189 248",
    },
  },
  {
    id: "royal-purple",
    name: "Royal Purple",
    nameAr: "بنفسجي ملكي",
    light: {
      primary: "76 29 149",
      "primary-light": "147 51 234",
      secondary: "192 132 252",
      "secondary-light": "216 180 254",
      bg: "250 248 255",
      surface: "255 255 255",
      "surface-alt": "243 237 254",
      text: "46 16 101",
      "text-muted": "107 85 140",
      border: "221 214 243",
      success: "5 150 105",
      warning: "217 119 6",
      severe: "234 88 12",
      danger: "220 38 38",
      info: "2 132 199",
    },
    dark: {
      primary: "192 132 252",
      "primary-light": "216 180 254",
      secondary: "232 200 255",
      "secondary-light": "243 226 255",
      bg: "13 7 30",
      surface: "30 20 60",
      "surface-alt": "50 35 85",
      text: "243 237 254",
      "text-muted": "168 148 200",
      border: "50 35 85",
      success: "52 211 153",
      warning: "251 191 36",
      severe: "251 146 60",
      danger: "248 113 113",
      info: "56 189 248",
    },
  },
  {
    id: "teal-tech",
    name: "Teal Tech",
    nameAr: "بترولي تقني",
    light: {
      primary: "19 78 74",
      "primary-light": "20 184 166",
      secondary: "94 234 212",
      "secondary-light": "153 246 228",
      bg: "247 254 253",
      surface: "255 255 255",
      "surface-alt": "240 253 250",
      text: "15 61 58",
      "text-muted": "75 107 104",
      border: "204 241 234",
      success: "5 150 105",
      warning: "217 119 6",
      severe: "234 88 12",
      danger: "220 38 38",
      info: "2 132 199",
    },
    dark: {
      primary: "94 234 212",
      "primary-light": "153 246 228",
      secondary: "167 243 227",
      "secondary-light": "204 251 241",
      bg: "4 20 18",
      surface: "19 50 47",
      "surface-alt": "30 74 70",
      text: "240 253 250",
      "text-muted": "148 200 194",
      border: "30 74 70",
      success: "52 211 153",
      warning: "251 191 36",
      severe: "251 146 60",
      danger: "248 113 113",
      info: "56 189 248",
    },
  },
  {
    id: "crimson",
    name: "Crimson",
    nameAr: "أحمر قرمزي",
    light: {
      primary: "127 29 29",
      "primary-light": "220 38 38",
      secondary: "252 165 165",
      "secondary-light": "254 202 202",
      bg: "255 249 249",
      surface: "255 255 255",
      "surface-alt": "254 242 242",
      text: "69 10 10",
      "text-muted": "120 70 70",
      border: "245 218 218",
      success: "5 150 105",
      warning: "217 119 6",
      severe: "234 88 12",
      danger: "185 28 28",
      info: "2 132 199",
    },
    dark: {
      primary: "252 165 165",
      "primary-light": "254 202 202",
      secondary: "254 215 215",
      "secondary-light": "254 226 226",
      bg: "25 5 5",
      surface: "60 20 20",
      "surface-alt": "85 30 30",
      text: "254 242 242",
      "text-muted": "200 150 150",
      border: "85 30 30",
      success: "52 211 153",
      warning: "251 191 36",
      severe: "251 146 60",
      danger: "248 113 113",
      info: "56 189 248",
    },
  },
];

/* ── الخطوط المتاحة ── */
export const AVAILABLE_FONTS = [
  { id: "tajawal", name: "Tajawal", nameAr: "تجوال", family: "'Tajawal', sans-serif" },
  { id: "cairo", name: "Cairo", nameAr: "القاهرة", family: "'Cairo', sans-serif" },
  { id: "ibm-plex-sans-arabic", name: "IBM Plex Sans Arabic", nameAr: "IBM بلكس عربي", family: "'IBM Plex Sans Arabic', sans-serif" },
  { id: "noto-sans-arabic", name: "Noto Sans Arabic", nameAr: "نوتو سانس عربي", family: "'Noto Sans Arabic', sans-serif" },
  { id: "noto-kufi-arabic", name: "Noto Kufi Arabic", nameAr: "نوتو كوفي عربي", family: "'Noto Kufi Arabic', sans-serif" },
];

/* ── القيم الافتراضية ── */
export const DEFAULT_THEME: ThemeSettings = {
  preset: "smart-blue",
  colors: {},
  fonts: {
    heading: "tajawal",
    body: "tajawal",
  },
  layout: {
    sidebarStyle: "full",
    contentMaxWidth: "7xl",
    cardRadius: "xl",
    density: "comfortable",
  },
  darkMode: {
    enabled: true,
    default: "light",
  },
  ui: {
    buttonStyle: "rounded",
    buttonSize: "md",
    iconStyle: "rounded",
    iconSize: "md",
  },
  custom_css: "",
};

export function getPreset(id: string): ThemePreset {
  return PRESETS.find((p) => p.id === id) ?? PRESETS[0];
}

export function getFontFamily(id: string): string {
  return AVAILABLE_FONTS.find((f) => f.id === id)?.family ?? "'Tajawal', sans-serif";
}
